import { useSelector, useDispatch } from 'react-redux';
import { z } from 'zod';
import { useRef } from 'react';
import { Delta } from 'quill/core';
import Quill from 'quill';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import { useCreateCommentMutation } from './comment.api.slice';
import { postCommentSchema } from './comment.schema';
import { useCommentForm } from './useCommentForm';
import { UserState } from '../auth/auth';
import { setShowLoginDialog } from '../global/global.slice';
import QuillEditor from '@/components/ui/quill-editor';
import { Textarea } from '@/components/ui/textarea';
import PostCommentFormHeader from '@/components/Comment/PostCommentFormHeader';

function PostCommentForm({ postId }: { postId: string }) {
  const dispatch = useDispatch();
  const [comment] = useCreateCommentMutation();
  const { isAuthenticated } = useSelector(
    (state: { auth: UserState }) => state.auth
  );
  const quillRef = useRef<Quill | null>(null);
  const commentForm = useCommentForm();

  async function handleCommentSubmit(
    values: z.infer<typeof postCommentSchema>
  ) {
    if (!isAuthenticated) {
      dispatch(setShowLoginDialog(true));
      return;
    }

    await comment({
      postId,
      comment: values.comment,
    }).unwrap();

    quillRef?.current?.setContents(new Delta().insert('\n'));
    commentForm.reset();
  }

  const toolbarOptions = [['bold', 'italic', 'underline', 'link']];

  return (
    <div className="w-full lg:w-2/3 border border-[#000] p-5 rounded-md h-fit gap-5 flex flex-col">
      <PostCommentFormHeader />
      <Form {...commentForm}>
        <form
          onSubmit={commentForm.handleSubmit(handleCommentSubmit)}
          className="flex flex-col gap-2"
        >
          <FormField
            control={commentForm.control}
            name="comment"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Textarea
                    placeholder="Enter your comments"
                    {...field}
                    className="hidden"
                  />
                </FormControl>
                <div className="flex flex-col gap-2 min-h-[300px]">
                  <QuillEditor
                    quillOptions={{
                      theme: 'snow',
                      modules: {
                        toolbar: toolbarOptions,
                      },
                    }}
                    placeholder="Write your comment"
                    onTextChange={(delta: Delta) => {
                      field.onChange(JSON.stringify(delta.ops));
                    }}
                    ref={quillRef}
                    readOnly={false}
                    className="min-h-[300px] border border-[#B4B7C9] rounded-md"
                  />
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex justify-end border-t border-[#D4D7E5] mt-5">
            <Button
              type="submit"
              variant="default"
              size="lg"
              className="rounded-md mt-5"
            >
              {isAuthenticated ? 'Post' : 'Login to Comment'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}

export default PostCommentForm;
